/**
 * ThreeWayMatchDialog — full-screen PO ↔ proof-of-delivery ↔ invoice review.
 *
 * The review is done against the *extracted* data from each document (not the
 * raw scans). The three documents render as aligned columns in PO → POD →
 * Invoice order, and every line item sits on the same horizontal row across all
 * three — so a reviewer scans a single line left-to-right to confirm it
 * reconciles, instead of eyeballing three PDFs.
 *
 * Presentational: the caller supplies the data + open state.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  EnvelopeIcon,
  BrainIcon,
  GitMergeIcon,
  ReceiptIcon,
  FileTextIcon,
  TruckIcon,
  type Icon,
} from '@phosphor-icons/react';
import { T } from 'gt-react';
import { cn } from '@/lib/utils';
import type {
  ExtractedDocument,
  ExtractedDocKind,
  ExtractedDocLineItem,
  ExtractedLineStatus,
} from './extracted-document';

export interface ThreeWayMatchRow {
  field: string;
  invoiceValue: string;
  poValue: string;
  podValue: string;
  /** false → rendered as a discrepancy. */
  match: boolean;
}

export interface ThreeWayMatchData {
  invoiceNumber: string;
  poNumber: string;
  /** Overall outcome — drives the headline badge. */
  status: 'matched' | 'discrepancy';
  /** One-line plain-English summary of the match result. */
  summary: string;
  /** Note explaining where the POD came from (e.g. a separate carrier email). */
  podSourceNote: string;
  /** Header-level field comparison; retained on the data, not rendered. */
  rows: ThreeWayMatchRow[];
  /** Extracted documents; rendered in PO → POD → Invoice order. */
  documents: ExtractedDocument[];
}

/** Columns read left-to-right: PO → POD → Invoice. */
const COLUMN_ORDER: ExtractedDocKind[] = [
  'purchase_order',
  'proof_of_delivery',
  'invoice',
];

const KIND_META: Record<ExtractedDocKind, { icon: Icon; tint: string }> = {
  invoice: { icon: ReceiptIcon, tint: 'text-teal-600' },
  purchase_order: { icon: FileTextIcon, tint: 'text-indigo-600' },
  proof_of_delivery: { icon: TruckIcon, tint: 'text-sky-600' },
};

function StatusMarker({ status }: { status?: ExtractedLineStatus | null }) {
  if (status === 'mismatch') {
    return <WarningIcon className="size-3.5 text-amber-500" weight="fill" />;
  }
  if (status === 'info') {
    return <InfoIcon className="size-3.5 text-blue-500" weight="fill" />;
  }
  if (status === 'match') {
    return (
      <CheckCircleIcon className="size-3.5 text-green-600" weight="fill" />
    );
  }
  return null;
}

/** Header-cell content for one document column: title, issuer, provenance, fields. */
function DocumentHeader({ doc }: { doc: ExtractedDocument }) {
  const { icon: KindIcon, tint } = KIND_META[doc.kind];

  return (
    <>
      <div className="flex items-center gap-2">
        <KindIcon className={cn('size-4 shrink-0', tint)} weight="fill" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{doc.title}</div>
          {doc.issuer && (
            <div className="truncate text-[11px] text-muted-foreground">
              {doc.issuer}
            </div>
          )}
        </div>
      </div>
      {doc.sourceNote && (
        <div className="mt-1.5 flex items-start gap-1 text-[11px] text-muted-foreground">
          <EnvelopeIcon className="mt-0.5 size-3 shrink-0" />
          <span>{doc.sourceNote}</span>
        </div>
      )}
      {doc.matchReason && (
        <div className="mt-1 flex items-start gap-1 text-[11px] text-muted-foreground">
          <BrainIcon className="mt-0.5 size-3 shrink-0" weight="fill" />
          <span>
            <span className="font-medium">
              <T context="prefix for an AI-generated reason a document matched">
                Why matched:
              </T>
            </span>{' '}
            {doc.matchReason}
          </span>
        </div>
      )}
      {doc.fields.length > 0 && (
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-[11px]">
          {doc.fields.map((f) => (
            <div key={f.label} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd
                className={cn(
                  'break-words text-right',
                  f.emphasize && 'font-semibold text-foreground',
                )}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}

/** The doc-specific metric columns (Qty / Unit / Amount, Shipped / Received…). */
function LineMetrics({
  doc,
  line,
}: {
  doc: ExtractedDocument;
  line: ExtractedDocLineItem;
}) {
  const { quantity, secondary, amount } = doc.columns;
  const parts: Array<{ label: string; value: string }> = [];
  if (quantity && line.quantity != null) {
    parts.push({ label: quantity, value: line.quantity });
  }
  if (secondary && line.secondary != null) {
    parts.push({ label: secondary, value: line.secondary });
  }
  if (amount && line.amount != null) {
    parts.push({ label: amount, value: line.amount });
  }
  if (parts.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
      {parts.map((p) => (
        <span key={p.label}>
          <span className="text-muted-foreground">{p.label} </span>
          <span className="font-medium text-foreground">{p.value}</span>
        </span>
      ))}
    </div>
  );
}

/** Line-cell content for one document column (or a dash when the line is absent). */
function DocumentLine({
  doc,
  line,
}: {
  doc: ExtractedDocument;
  line?: ExtractedDocLineItem;
}) {
  if (!line) {
    return <span className="text-[11px] italic text-muted-foreground">—</span>;
  }

  return (
    <div className="flex items-start gap-1.5">
      <span className="mt-0.5 shrink-0">
        <StatusMarker status={line.status} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          {line.number != null && (
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
              #{line.number}
            </span>
          )}
          <span className="text-xs font-medium">{line.description}</span>
        </div>
        {line.note && (
          <div className="text-[11px] text-muted-foreground">{line.note}</div>
        )}
        <LineMetrics doc={doc} line={line} />
      </div>
    </div>
  );
}

/**
 * The three documents as an aligned comparison TABLE: a header row of document
 * columns (PO → POD → Invoice) and one row per line item, so line `i` of all
 * three sits on the same band. Plain grid rules — no per-cell cards.
 */
function AlignedDocuments({ documents }: { documents: ExtractedDocument[] }) {
  const docs = COLUMN_ORDER.map((kind) =>
    documents.find((d) => d.kind === kind),
  ).filter((d): d is ExtractedDocument => d != null);

  if (docs.length === 0) return null;

  const lineCount = docs.reduce(
    (max, d) => Math.max(max, d.lineItems.length),
    0,
  );

  return (
    <div className="overflow-hidden rounded-lg border-l border-t">
      <div
        className="grid items-stretch"
        style={{
          gridTemplateColumns: `repeat(${docs.length}, minmax(0, 1fr))`,
        }}
      >
        {docs.map((doc) => (
          <div
            key={`head-${doc.kind}`}
            className="border-b border-r bg-muted/50 p-2.5"
          >
            <DocumentHeader doc={doc} />
          </div>
        ))}
        {Array.from({ length: lineCount }).map((_, i) =>
          docs.map((doc) => (
            <div
              key={`line-${doc.kind}-${i}`}
              className="border-b border-r p-2.5"
            >
              <DocumentLine doc={doc} line={doc.lineItems[i]} />
            </div>
          )),
        )}
      </div>
    </div>
  );
}

export function ThreeWayMatchDialog({
  open,
  onOpenChange,
  data,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ThreeWayMatchData;
}) {
  const isMatched = data.status === 'matched';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // True fullscreen overlay — override the centered-modal defaults.
          'inset-0 left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0',
          'flex flex-col gap-0 rounded-none border-0 p-0 sm:max-w-none',
        )}
      >
        <DialogHeader className="border-b px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <GitMergeIcon className="size-5 text-sky-600" />
            <DialogTitle className="text-base">
              <T context="3-way match dialog title">3-way match</T>
            </DialogTitle>
            <Badge variant="outline" className="gap-1 text-xs">
              {data.invoiceNumber} · {data.poNumber}
            </Badge>
            <Badge
              className={cn(
                'gap-1 text-xs',
                isMatched
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-amber-300 bg-amber-50 text-amber-700',
              )}
            >
              {isMatched ? (
                <CheckCircleIcon className="size-3.5" weight="fill" />
              ) : (
                <WarningIcon className="size-3.5" weight="fill" />
              )}
              {isMatched ? (
                <T context="3-way match status">Fully reconciled</T>
              ) : (
                <T context="3-way match status">Discrepancies found</T>
              )}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{data.summary}</p>
          {data.podSourceNote && (
            <div className="flex items-center gap-1.5 text-xs text-sky-700">
              <EnvelopeIcon className="size-3.5" />
              {data.podSourceNote}
            </div>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <AlignedDocuments documents={data.documents} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
